import React, { useEffect, useState } from "react";
import { BellIcon, WrenchScrewdriverIcon } from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import {
  ensurePushSubscription,
  unsubscribePush,
  getPermissionStatus,
  isPushSupported,
} from "../utils/push";
import { toastError, toastSuccess } from "../utils/toast";
import { api } from "../utils/api";

const Notifiche = () => {
  const {
    user,
    activeCongregazione,
    pushStatus,
    pushInvite,
    ackPushInvite,
    refreshPushStatus,
  } = useAuth();
  const [pushToggleLoading, setPushToggleLoading] = useState(false);
  const [pushSettings, setPushSettings] = useState({
    reminder_enabled: true,
    reminder_12h_enabled: true,
    reminder_interval_min: 15,
  });
  const [pushSettingsLoading, setPushSettingsLoading] = useState(false);
  const [pushSettingsSaving, setPushSettingsSaving] = useState(false);
  const [pushBroadcastLoading, setPushBroadcastLoading] = useState(false);
  const [pushBroadcastResult, setPushBroadcastResult] = useState(null);
  const [showPushHelp, setShowPushHelp] = useState(false);
  const [pushBroadcastForm, setPushBroadcastForm] = useState({
    title: "Comunicazione Planner Turni",
    body: "",
    url: "https://plannerturni.it",
    onlyActiveCongregation: false,
  });
  const pushSupported = isPushSupported();
  const pushPermission = getPermissionStatus();
  const isiOS = /iP(hone|od|ad)/i.test(navigator.userAgent);
  const isStandalone =
    typeof window !== "undefined" && window.navigator.standalone;
  const hasActivePush = (pushStatus?.subscriptions || 0) > 0;
  const showPushInvite = pushInvite?.pending && !hasActivePush;

  useEffect(() => {
    if (user?.ruolo === "admin" || user?.ruolo === "super_admin") {
      loadPushSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.ruolo, activeCongregazione?.id]);

  const loadPushSettings = async () => {
    setPushSettingsLoading(true);
    try {
      if (!activeCongregazione?.id) {
        throw new Error("Congregazione non selezionata");
      }
      const { data } = await api.get("/push/settings", {
        headers: { "x-congregazione-id": activeCongregazione.id },
      });
      if (data) {
        setPushSettings({
          reminder_enabled: data.reminder_enabled !== false,
          reminder_12h_enabled: data.reminder_12h_enabled !== false,
          reminder_interval_min:
            parseInt(data.reminder_interval_min, 10) || 15,
        });
      }
    } catch (error) {
      console.error("Errore lettura impostazioni push:", error);
      toastError(
        error.response?.data?.message ||
          "Errore durante la lettura delle impostazioni push"
      );
    } finally {
      setPushSettingsLoading(false);
    }
  };

  const savePushSettings = async (e) => {
    e.preventDefault();
    if (pushSettingsSaving) return;
    setPushSettingsSaving(true);
    try {
      if (!activeCongregazione?.id) {
        throw new Error("Congregazione non selezionata");
      }
      const payload = {
        reminder_enabled: Boolean(pushSettings.reminder_enabled),
        reminder_12h_enabled: Boolean(pushSettings.reminder_12h_enabled),
        reminder_interval_min: Math.min(
          180,
          Math.max(5, parseInt(pushSettings.reminder_interval_min, 10) || 15)
        ),
      };
      const { data } = await api.post("/push/settings", payload, {
        headers: { "x-congregazione-id": activeCongregazione.id },
      });
      toastSuccess(data?.message || "Impostazioni aggiornate");
    } catch (error) {
      console.error("Errore salvataggio impostazioni push:", error);
      toastError(
        error.response?.data?.message ||
          "Errore durante il salvataggio delle impostazioni push"
      );
    } finally {
      setPushSettingsSaving(false);
    }
  };

  const handlePushBroadcast = async (e) => {
    e.preventDefault();
    setPushBroadcastResult(null);
    if (!pushBroadcastForm.body.trim()) {
      toastError("Inserisci un messaggio per la notifica");
      return;
    }
    setPushBroadcastLoading(true);
    try {
      const payload = {
        title: pushBroadcastForm.title.trim() || "Comunicazione Planner Turni",
        body: pushBroadcastForm.body.trim(),
      };
      if (pushBroadcastForm.url?.trim()) {
        payload.url = pushBroadcastForm.url.trim();
      }
      if (pushBroadcastForm.onlyActiveCongregation && activeCongregazione?.id) {
        payload.congregazione_id = activeCongregazione.id;
      }

      const { data } = await api.post("/push/broadcast", payload);
      setPushBroadcastResult(data);
      toastSuccess(data.message || "Notifica inviata");
    } catch (error) {
      console.error("Errore invio push broadcast:", error);
      toastError(
        error.response?.data?.message ||
          "Errore durante l'invio della notifica push"
      );
    } finally {
      setPushBroadcastLoading(false);
    }
  };

  const handleEnablePush = async () => {
    setPushToggleLoading(true);
    try {
      await ensurePushSubscription({
        congregazioneId: activeCongregazione?.id,
      });
      await refreshPushStatus();
      if (pushInvite?.pending) {
        await ackPushInvite();
      }
      toastSuccess("Notifiche push attivate su questo dispositivo");
    } catch (error) {
      console.error("Errore attivazione push:", error);
      toastError(
        error.message ||
          "Impossibile attivare le notifiche push su questo dispositivo"
      );
    } finally {
      setPushToggleLoading(false);
    }
  };

  const handleDisablePush = async () => {
    setPushToggleLoading(true);
    try {
      await unsubscribePush();
      await refreshPushStatus();
      toastSuccess("Notifiche push disattivate su questo dispositivo");
    } catch (error) {
      console.error("Errore disattivazione push:", error);
      toastError(
        error.response?.data?.message ||
          "Impossibile disattivare le notifiche push su questo dispositivo"
      );
    } finally {
      setPushToggleLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="mb-4">
        <h1 className="text-3xl font-bold text-gray-900">Notifiche</h1>
        <p className="text-gray-600">Gestisci le notifiche push e i reminder.</p>
        <div className="accent-line mt-4" />
      </div>

      <div className="bg-white shadow-lg rounded-lg p-6 relative overflow-hidden">
        <span className="accent-bar top-6 bottom-6" aria-hidden="true" />
        <div className="flex flex-col gap-2 mb-4 sm:mb-3">
          <div className="flex items-center gap-2">
            <BellIcon className="h-6 w-6 text-gray-600" />
            <h2 className="text-xl font-semibold text-gray-900">Notifiche push</h2>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                pushStatus?.subscriptions > 0
                  ? "bg-green-100 text-green-700"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              {pushStatus?.subscriptions > 0 ? "Push attive" : "Push inattive"}
            </span>
            <button
              type="button"
              onClick={refreshPushStatus}
              className="text-xs text-primary-700 hover:underline"
            >
              Aggiorna
            </button>
          </div>
        </div>

        {!pushSupported ? (
          <p className="text-sm text-red-600">
            Le notifiche push non sono supportate su questo dispositivo/browser.
          </p>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              Stato:{" "}
              <span className="font-semibold">
                {hasActivePush
                  ? "attive su questo account"
                  : "non attive su questo dispositivo"}
              </span>{" "}
              {hasActivePush &&
                `(${pushStatus.subscriptions} subscription registrate)`}
            </p>

            {showPushInvite && (
              <div className="border border-blue-200 bg-blue-50 text-blue-800 rounded-md p-3 text-sm flex flex-col gap-2">
                <div className="font-semibold">Richiesta di attivazione notifiche</div>
                <div>
                  {pushInvite.message || "Un amministratore ti invita ad attivare le push."}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={handleEnablePush}
                    className="px-3 py-1.5 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm"
                  >
                    Attiva ora
                  </button>
                  <button
                    type="button"
                    onClick={ackPushInvite}
                    className="px-3 py-1.5 border border-gray-300 rounded-md text-sm hover:bg-gray-50"
                  >
                    Ignora
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowPushHelp((prev) => !prev)}
                    className="px-3 py-1.5 border border-blue-200 text-blue-800 rounded-md text-sm hover:bg-blue-100"
                  >
                    Istruzioni
                  </button>
                </div>
                {showPushHelp && (
                  <div className="bg-white/70 border border-blue-200 rounded-md p-3 text-gray-800 text-xs space-y-3">
                    <div className="font-semibold text-sm">Come attivare le push</div>
                    <div>
                      <div className="font-semibold">Da smartphone Android</div>
                      <ol className="list-decimal list-inside space-y-1">
                        <li>Apri il sito da Chrome.</li>
                        <li>Menu ⋮ → “Aggiungi a schermata Home”.</li>
                        <li>Apri l’icona creata e consenti le notifiche quando richiesto.</li>
                        <li>Premi “Attiva push su questo dispositivo”.</li>
                      </ol>
                    </div>
                    <div>
                      <div className="font-semibold">Da iPhone</div>
                      <ol className="list-decimal list-inside space-y-1">
                        <li>Apri il sito da Safari.</li>
                        <li>Tocca Condividi → “Aggiungi a Home”. (È indispensabile: senza PWA su Home le push non funzionano)</li>
                        <li>Apri l’icona sulla Home e consenti le notifiche quando richiesto.</li>
                        <li>Torna qui e premi “Attiva push su questo dispositivo”.</li>
                      </ol>
                    </div>
                    <div>
                      <div className="font-semibold">Da PC/Mac</div>
                      <ol className="list-decimal list-inside space-y-1">
                        <li>Apri il sito su Chrome/Edge/Firefox.</li>
                        <li>Consenti le notifiche dal lucchetto/permessi vicino alla barra degli indirizzi.</li>
                        <li>(Opzionale) Su Chrome: menu ⋮ → “Installa app” per usarlo come app.</li>
                        <li>Premi “Attiva push su questo dispositivo”.</li>
                      </ol>
                    </div>
                  </div>
                )}
              </div>
            )}

            {isiOS && !isStandalone && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
                Su iOS devi usare l'app aggiunta alla schermata Home per attivare le push (Safari → Condividi → Aggiungi a Home).
              </div>
            )}

            {pushPermission === "denied" && (
              <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2">
                Permesso notifiche negato. Abilitalo dalle impostazioni del browser e riprova.
              </div>
            )}

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={handleEnablePush}
                disabled={pushToggleLoading}
                className="w-full sm:w-auto px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {pushToggleLoading ? "Attivazione..." : "Attiva push su questo dispositivo"}
              </button>
              <button
                type="button"
                onClick={handleDisablePush}
                disabled={pushToggleLoading}
                className="w-full sm:w-auto px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Disattiva push su questo dispositivo
              </button>
            </div>
          </div>
        )}
      </div>

      {(user?.ruolo === "admin" || user?.ruolo === "super_admin") && (
        <div className="bg-white shadow-lg rounded-lg p-6 relative overflow-hidden">
          <span className="accent-bar top-6 bottom-6" aria-hidden="true" />
          <div className="flex items-center mb-4">
            <WrenchScrewdriverIcon className="h-6 w-6 text-gray-600 mr-2" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Reminder push</h2>
              <p className="text-sm text-gray-500">
                Abilita/disabilita i reminder automatici e imposta l'intervallo di scansione.
              </p>
            </div>
          </div>
          <form
            className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end"
            onSubmit={savePushSettings}
          >
            <div className="flex flex-col gap-2 text-sm text-gray-700">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                  checked={pushSettings.reminder_enabled}
                  onChange={(e) =>
                    setPushSettings((prev) => ({
                      ...prev,
                      reminder_enabled: e.target.checked,
                    }))
                  }
                  disabled={pushSettingsLoading || pushSettingsSaving}
                />
                Reminder 24h attivo
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                  checked={pushSettings.reminder_12h_enabled}
                  onChange={(e) =>
                    setPushSettings((prev) => ({
                      ...prev,
                      reminder_12h_enabled: e.target.checked,
                    }))
                  }
                  disabled={pushSettingsLoading || pushSettingsSaving}
                />
                Reminder 12h attivo
              </label>
            </div>
            <label className="flex flex-col text-sm text-gray-700">
              Intervallo scansione (minuti)
              <input
                type="number"
                min={5}
                max={180}
                step={5}
                value={pushSettings.reminder_interval_min}
                onChange={(e) =>
                  setPushSettings((prev) => ({
                    ...prev,
                    reminder_interval_min: e.target.value,
                  }))
                }
                disabled={pushSettingsLoading || pushSettingsSaving}
                className="mt-1 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </label>
            <div className="sm:col-span-2 flex gap-2">
              <button
                type="submit"
                disabled={pushSettingsLoading || pushSettingsSaving}
                className="btn-primary"
              >
                {pushSettingsSaving ? "Salvataggio..." : "Salva impostazioni"}
              </button>
              {pushSettingsLoading && (
                <span className="text-xs text-gray-500 self-center">
                  Caricamento impostazioni...
                </span>
              )}
            </div>
          </form>
        </div>
      )}

      {user?.ruolo === "super_admin" && (
        <div className="bg-white shadow-lg rounded-lg p-6 relative overflow-hidden">
          <span className="accent-bar top-6 bottom-6" aria-hidden="true" />
          <div className="flex items-center mb-4">
            <BellIcon className="h-6 w-6 text-primary-600 mr-2" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Notifiche Push (Super Admin)
              </h2>
              <p className="text-sm text-gray-500">
                Invia una notifica push a tutte le subscription valide o solo alla congregazione attiva.
              </p>
            </div>
          </div>

          <form className="space-y-4" onSubmit={handlePushBroadcast}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Titolo
              </label>
              <input
                type="text"
                value={pushBroadcastForm.title}
                onChange={(e) =>
                  setPushBroadcastForm((prev) => ({
                    ...prev,
                    title: e.target.value,
                  }))
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                maxLength={120}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Messaggio
              </label>
              <textarea
                value={pushBroadcastForm.body}
                onChange={(e) =>
                  setPushBroadcastForm((prev) => ({
                    ...prev,
                    body: e.target.value,
                  }))
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                rows={3}
                maxLength={500}
                placeholder="Testo della notifica"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                URL (opzionale)
              </label>
              <input
                type="url"
                value={pushBroadcastForm.url}
                onChange={(e) =>
                  setPushBroadcastForm((prev) => ({
                    ...prev,
                    url: e.target.value,
                  }))
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="https://plannerturni.it"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                id="push-only-active"
                type="checkbox"
                checked={pushBroadcastForm.onlyActiveCongregation}
                onChange={(e) =>
                  setPushBroadcastForm((prev) => ({
                    ...prev,
                    onlyActiveCongregation: e.target.checked,
                  }))
                }
                className="h-4 w-4 text-primary-600 border-gray-300 rounded"
              />
              <label htmlFor="push-only-active" className="text-sm text-gray-700">
                Invia solo alla congregazione attiva
              </label>
              {!activeCongregazione?.id && pushBroadcastForm.onlyActiveCongregation && (
                <span className="text-xs text-amber-600">
                  Seleziona una congregazione per filtrare.
                </span>
              )}
            </div>

            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={pushBroadcastLoading}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {pushBroadcastLoading ? "Invio..." : "Invia notifica push"}
              </button>
              {pushBroadcastResult && (
                <span className="text-xs text-gray-600">
                  Inviate: {pushBroadcastResult.sent || 0} / {pushBroadcastResult.total || 0}{" "}
                  {pushBroadcastResult.failed ? `(fallite: ${pushBroadcastResult.failed})` : ""}
                </span>
              )}
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Notifiche;
